import React from "react";
import { Stack, Button, Typography, FormNumberField, TextField, Box } from "@datanomik/commons-ui";
import { UseFormReturn, Controller } from "react-hook-form";
import { Chip, InputAdornment } from "@mui/material";
import { MenuItem, Select } from "@datanomik/commons-ui";
import { useTranslation } from "react-i18next";
import { SelectedAccountInput } from "../../rule-creation/rule-creation-form/components/CashPoolingAccountAutocomplete/SelectedAccountInput";
import { TransferAccountAutocomplete } from "../components/TransferAccountAutocomplete";
import { useAccounts } from "../../hooks";
import type { AccountResponse, AccountType } from "../../types/account.types";
import type { DirectTransferFormData } from "../useDirectTransferForm";

const TRANSFER_MODE_LABELS: Record<string, string> = {
  PIX: "PIX",
  BANK_TRANSFER: "TED",
};

const FieldLabel: React.FC<{ children: React.ReactNode; optional?: string }> = ({ children, optional }) => (
  <Typography
    fontSize={13.5}
    fontWeight={500}
    sx={{ mb: 1 }}
  >
    {children}
    {optional && (
      <Typography component="span" fontSize={13.5} fontWeight={400} color="text.disabled">
        {" "}({optional})
      </Typography>
    )}
  </Typography>
);

const InlineAccountField: React.FC<{
  name: string;
  control: any;
  accountType: AccountType;
  fiscalNumber?: string;
  onAccountDataChange: (account: AccountResponse | null) => void;
  excludedAccountIds?: string[];
}> = ({ name, control, accountType, fiscalNumber, onAccountDataChange, excludedAccountIds = [] }) => {
  const { data: accounts } = useAccounts({ type: accountType, fiscalNumber });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const account = accounts?.content.find(
          (acc) => acc.account.accountId === field.value,
        );

        if (account) {
          return (
            <SelectedAccountInput
              account={account}
              onEdit={() => {
                field.onChange(null);
                onAccountDataChange(null);
              }}
            />
          );
        }

        return (
          <TransferAccountAutocomplete
            value={field.value as string | null}
            onChange={(accountId) => {
              field.onChange(accountId);
            }}
            accountType={accountType}
            fiscalNumber={fiscalNumber}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            onAccountDataChange={onAccountDataChange}
            excludedAccountIds={excludedAccountIds}
          />
        );
      }}
    />
  );
};

interface DirectTransferDetailsStepProps {
  form: UseFormReturn<DirectTransferFormData>;
  onContinue: () => void;
  handleOriginAccountChange: (account: AccountResponse | AccountResponse[] | null) => void;
  handleDestinationAccountChange: (account: AccountResponse | AccountResponse[] | null) => void;
  originFiscalNumber?: string;
  transferModes: string[];
}

export const DirectTransferDetailsStep: React.FC<DirectTransferDetailsStepProps> = ({
  form,
  onContinue,
  handleOriginAccountChange,
  handleDestinationAccountChange,
  originFiscalNumber,
  transferModes,
}) => {
  const { t } = useTranslation();

  const originAccountValue = form.watch("originAccount");
  const destinationAccountValue = form.watch("destinationAccount");

  const originExcludedIds = destinationAccountValue ? [destinationAccountValue] : [];
  const destinationExcludedIds = originAccountValue ? [originAccountValue] : [];
  return (
    <form onSubmit={onContinue} style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, pt: 2.5, pb: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>

        <Stack spacing={0}>
          <FieldLabel>{t("Origin Account")}</FieldLabel>
          <Box sx={{ mt: 0.25 }}>
            <InlineAccountField
              name="originAccount"
              control={form.control}
              accountType="ORIGIN"
              onAccountDataChange={handleOriginAccountChange}
              excludedAccountIds={originExcludedIds}
            />
          </Box>
        </Stack>

        <Stack spacing={0} sx={{ opacity: originAccountValue ? 1 : 0.45, pointerEvents: originAccountValue ? "auto" : "none", transition: "opacity 0.2s" }}>
          <FieldLabel>{t("Destination Account")}</FieldLabel>
          <Box sx={{ mt: 0.25 }}>
            <InlineAccountField
              name="destinationAccount"
              control={form.control}
              accountType="DESTINATION"
              fiscalNumber={originFiscalNumber}
              onAccountDataChange={handleDestinationAccountChange}
              excludedAccountIds={destinationExcludedIds}
            />
          </Box>
        </Stack>

        <Stack>
          <FieldLabel>{t("Mode")}</FieldLabel>
          {transferModes.length > 1 ? (
            <Controller
              name="transferMode"
              control={form.control}
              rules={{ required: t("Select the transfer mode") }}
              render={({ field }) => (
                <Select {...field} size="small" fullWidth displayEmpty>
                  {transferModes.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {TRANSFER_MODE_LABELS[mode] ?? mode}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          ) : transferModes.length === 1 ? (
            <Chip
              label={TRANSFER_MODE_LABELS[transferModes[0]] ?? transferModes[0]}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: 12, px: 0.5, width: "fit-content" }}
            />
          ) : (
            <Select size="small" fullWidth displayEmpty disabled value="">
              <MenuItem value="" disabled>
                <Typography color="text.disabled" fontSize={13.5}>{t("Transfer Type")}</Typography>
              </MenuItem>
            </Select>
          )}
        </Stack>

        <Stack>
          <FieldLabel>{t("Amount")}</FieldLabel>
          <FormNumberField
            name="amount"
            control={form.control as any}
            placeholder={t("Amount to transfer")}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography fontWeight={600} color="text.disabled">R$</Typography>
                  </InputAdornment>
                ),
              },
              htmlInput: {
                autoComplete: "new-password",
              },
            }}
            maskOptions={{
              min: 0,
            }}
            validationRules={{
              validate: {
                greaterThan0: (value: number) => {
                  return value > 0 || t("The number must be greater than 0");
                },
              },
            }}
          />
        </Stack>

        <Stack>
          <FieldLabel optional={t("optional")}>{t("Description")}</FieldLabel>
          <Controller
            name="description"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                rows={3}
                placeholder={t("Add a description...")}
                fullWidth
                sx={{ "& textarea": { resize: "none", lineHeight: 1.5 } }}
              />
            )}
          />
        </Stack>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.100", flexShrink: 0 }}
      >
        <Button
          type="submit"
          variant="contained"
          sx={{ px: 4, py: 1.375 }}
        >
          {t("Continue")}
        </Button>
      </Stack>
    </form>
  );
};
